import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import { ContentCompetency } from '@backrs_ui/common-ui';

import { fetchWrapper as fetch } from '@backrs_ui/grow-ui/src/utils/fetch/fetchWrapper';

import { RootState } from '../store';

const ENVIRONMENT_URL = process.env.REACT_APP_API_BASEURL;

type ContentCompetencyState = Readonly<{
  contentCompetencies: ContentCompetency[];
  loading: boolean;
  error?: string;
}>;

const initialState: ContentCompetencyState = {
  contentCompetencies: [],
  loading: false,
};

export const fetchContentCompetencies = createAsyncThunk(
  'contentCompetency/fetchContentCompetencies',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(`${ENVIRONMENT_URL}/content-competency`);

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();

      return data as ContentCompetency[];
    } catch (error) {
      return rejectWithValue(`Failed to fetch content competencies with error: ${error}`);
    }
  },
);

export const contentCompetencySlice = createSlice({
  name: 'contentCompetency',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchContentCompetencies.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(
        fetchContentCompetencies.fulfilled,
        (state, action: PayloadAction<ContentCompetency[]>) => {
          state.contentCompetencies = action.payload;
          state.loading = false;
        },
      )
      .addCase(fetchContentCompetencies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

// Other code such as selectors can use the imported `RootState` type
export const selectContentCompetencies = (state: RootState) => state.contentCompetency;

export const contentCompetencyReducer = contentCompetencySlice.reducer;

export const selectCompetencyByTitle = (title: string) => (state: RootState) =>
  state.contentCompetency.contentCompetencies.find((c) => c.title === title);
